export type Emotion = "neutral" | "happy" | "soft";

export type VisualState =
  | "idle"
  | "listening"
  | "thinking"
  | "speaking-neutral"
  | "speaking-happy"
  | "speaking-soft"
  | "interrupted";

export type CallEvent =
  | { type: "user_speech_started" }
  | { type: "user_speech_ended"; transcript?: string }
  | { type: "llm_response_ready"; responseText: string; emotion?: Emotion }
  | { type: "tts_audio_started"; responseText: string; emotion?: Emotion }
  | { type: "tts_audio_done" }
  | { type: "user_interrupt" }
  | { type: "error"; message?: string };

export interface CallSession {
  visualState: VisualState;
  lastEmotion: Emotion;
  isUserSpeaking: boolean;
  isSpeaking: boolean;
  userTranscript: string;
  assistantSubtitle: string;
  errorMessage: string | null;
  updatedAt: number;
}

export function createInitialCallSession(now = Date.now()): CallSession {
  return {
    visualState: "idle",
    lastEmotion: "neutral",
    isUserSpeaking: false,
    isSpeaking: false,
    userTranscript: "",
    assistantSubtitle: "",
    errorMessage: null,
    updatedAt: now
  };
}

export function speakingStateForEmotion(emotion: Emotion = "neutral"): VisualState {
  if (emotion === "happy") return "speaking-happy";
  if (emotion === "soft") return "speaking-soft";
  return "speaking-neutral";
}

export function reduceCallEvent(
  session: CallSession,
  event: CallEvent,
  now = Date.now()
): CallSession {
  const base = {
    ...session,
    errorMessage: null,
    updatedAt: now
  };

  switch (event.type) {
    case "user_speech_started":
      return {
        ...base,
        visualState: "listening",
        isUserSpeaking: true,
        isSpeaking: false
      };
    case "user_speech_ended":
      return {
        ...base,
        visualState: "thinking",
        isUserSpeaking: false,
        userTranscript: event.transcript?.trim() || session.userTranscript
      };
    case "llm_response_ready":
      return {
        ...base,
        visualState: "thinking",
        lastEmotion: event.emotion ?? "neutral",
        assistantSubtitle: event.responseText.trim()
      };
    case "tts_audio_started": {
      const emotion = event.emotion ?? "neutral";
      return {
        ...base,
        visualState: speakingStateForEmotion(emotion),
        lastEmotion: emotion,
        isSpeaking: true,
        assistantSubtitle: event.responseText.trim()
      };
    }
    case "tts_audio_done":
      return {
        ...base,
        visualState: "idle",
        isSpeaking: false,
        isUserSpeaking: false
      };
    case "user_interrupt":
      return {
        ...base,
        visualState: "interrupted",
        isSpeaking: false,
        isUserSpeaking: false
      };
    case "error":
      return {
        ...base,
        visualState: "idle",
        isSpeaking: false,
        isUserSpeaking: false,
        errorMessage: event.message ?? "通话暂时不可用"
      };
    default:
      return base;
  }
}
